/**
 * useInvoiceValidation
 * --------------------
 * Memoized client-side validation. Returns:
 *  - issues: full list (errors + warnings)
 *  - errors: blocking only
 *  - hasErrors: convenience boolean
 *  - invalidFields: Set<string> for inline highlighting
 *
 * Use `attempted` to defer surfacing errors until the user has tried to
 * submit at least once — avoids screaming red borders on a fresh form.
 */
import { useMemo } from "react";
import {
  validateInvoice,
  getBlockingErrors,
  type InvoiceValidationInput,
  type ValidationIssue,
} from "@/lib/invoiceValidation";

export function useInvoiceValidation(input: InvoiceValidationInput) {
  return useMemo(() => {
    const issues = validateInvoice(input);
    const errors = getBlockingErrors(issues);
    const invalidFields = new Set(errors.map((e) => e.field));
    return {
      issues,
      errors,
      hasErrors: errors.length > 0,
      invalidFields,
    };
    // Stringify-stable: avoid recomputing on identity-only changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(input)]);
}

export type { ValidationIssue };
